/* eslint-disable no-await-in-loop */
import { uploadRequest } from '@/shared/request-configs/upload-request';
import { IEditImage, TFileUploadType } from '@/shared';
import { IS3ResponseData, IUploadResponse } from '@/shared/interfaces/upload-response';
import app from '@/main';
import appEnvConfig, { maxUploadSize } from '@/config';
import { TFileTypeMediaUploads } from '../types/file-types';

/**
 * Determine public URL of an uploaded file response (if bucket matches, build it from config)
 * @param {IS3ResponseData} response - response data from the specific file (full/thumb)
 */
const uploadedFileUrl = (response: IS3ResponseData) => {
  if (appEnvConfig && response.Bucket === appEnvConfig.s3Bucket) {
    return `${appEnvConfig.s3Url}/${response.Key}`;
  }
  return response.Location;
};

/**
 * Fetches Types for clientKey <br>
 * API Endpoints {@link uploadRequest.get}
 * @param {string} clientKey - key of the client
 */
export const getTypes = (clientKey: string) => uploadRequest.get(clientKey);

/**
 * Validates Uploads by maxUploadSize
 * @see maxUploadSize
 * @param {FileList} files - files to be validated
 */
export const validateUploads = (files: FileList) => {
  const vm = app();

  for (let i = 0; i < files.length; i += 1) {
    const size = files.item(i)?.size || 0;
    if (size > maxUploadSize) {
      const message = `'${files.item(i)?.name}' is too large to upload!`
        + `Max allowed size is ${(maxUploadSize / 1000000)}Mb.`;

      if (vm) {
        vm.$eventBus.$emit('show-error-toast', { message, title: 'Upload file size limit exceeded!' });
      }

      throw new Error(message);
    }
  }
};

/**
 * Breaks down FileList into chunks of FormData[]
 * @see maxUploadSize
 * @param files - to be broken down
 */
export const createChunks = (files: FileList): FormData[] => {
  const chunks: FormData[] = [];
  let data = new FormData();
  let currentSize = 0;

  for (let i = 0; i < files.length; i += 1) {
    currentSize += files.item(i)!.size;

    if (currentSize > maxUploadSize) {
      chunks.push(data);
      data = new FormData();
      currentSize = 0;
    }

    data.append('files', files.item(i)!);
  }

  chunks.push(data);
  return chunks;
};

/**
 * Updates Image <br>
 * API Endpoint - {@link uploadRequest.put}
 * @param {string} clientKey - key of the client
 * @param {string} filename - name of the image
 * @param {TFileUploadType} type - type of file upload type
 * @param {IEditImage[]}data - image for update
 * @param {string} [querystring=''] - additional filters, empty by default
 */
export const updateImage = (
  clientKey: string,
  filename: string,
  type: TFileUploadType,
  data: IEditImage[],
  querystring: string = '',
) => uploadRequest.put(clientKey, type, filename, data, querystring);

/**
 * Updates Image and returns as new image <br>
 * API Endpoint - {@link uploadRequest.put}
 * @param {string} clientKey - key of the client
 * @param {string} filename - name of the image
 * @param {TFileUploadType} type - type of file upload type
 * @param {IEditImage[]}data - image for update
 * @param {string} [querystring=''] - additional filters, empty by default
 */
export const updateImageAsNew = async (
  clientKey: string,
  filename: string,
  type: TFileUploadType,
  data: IEditImage[],
  querystring: string = '',
) => {
  const response = await uploadRequest.put(clientKey, type, filename, data, true, querystring);

  return { filename: uploadedFileUrl(response.full), ...response.meta && response.meta };
};

/**
 * Uploads Files <br>
 * API Endpoint - {@link uploadRequest.post}
 * @param {string} clientKey - key of the client
 * @param {TFileUploadType} type - type of the upload
 * @param {FileList} files - files for upload
 * @param {string} [querystring=''] - additional filters, empty by default
 */
export const uploadFiles = async (
  clientKey: string,
  type: TFileUploadType,
  files: FileList,
  querystring: string = '',
) => {
  validateUploads(files);
  const chunkFormDatas = createChunks(files);
  const uploadResults: IUploadResponse[] = [];

  for (let i = 0; i < chunkFormDatas.length; i++) {
    const uploads = await uploadRequest.post(clientKey, type, chunkFormDatas[i], querystring);
    uploadResults.push(...uploads);
  }

  return uploadResults;
};

/**
 * Uploads Single File <br>
 * API Endpoint - `/userfiles/browse` <br> via {@link uploadRequest.get}, {@link uploadRequest.post}
 * @param {string} clientKey - key of the client
 * @param {TFileUploadType} type - type of the upload
 * @param {File} file - file for the upload
 * @param {string} [querystring=''] - filter query, by default empty
 */
export const uploadSingleFile = async (
  clientKey: string,
  type: TFileUploadType,
  file: File,
  querystring: string = '',
): Promise<{ filename: string, width: string, height: string }> => {
  const data = new FormData();
  data.append('files', file);
  const uploads = await uploadRequest.post(clientKey, type, data, querystring);
  return { filename: uploadedFileUrl(uploads[0].full), ...uploads[0].meta as { width: string, height: string } };
};
/**
 * Fetches User Files <br>
 * API Endpoint - `/userfiles/browse` <br> via {@link uploadRequest.get}
 * @param clientKey - key of the client
 */
export const getUserfiles = (clientKey: string) => uploadRequest.get(clientKey, `/userfiles/browse`);

/**
 * Uploads User Files <br>
 * @see uploadFiles
 * @param clientKey - key of the client
 * @param files - files for upload
 */
export const uploadUserfiles = (clientKey: string, files: FileList) => uploadFiles(clientKey, 'userfiles', files);

/**
 * Deletes a userfile from S3.
 * @param filename
 * @returns S3 File headers of the deleted file.
 */
export const removeUserFile = (clientKey: string, filename: string) => uploadRequest.delete(clientKey, `/userfiles/${filename}`);

export default {
  getTypes,
  updateImage,
  updateImageAsNew,
  uploadFiles,
  uploadUserfiles,
  uploadSingleFile,
  getUserfiles,
  removeUserFile,
};
